import { useMutation, useQuery, useQueryClient } from 'react-query';

import axiosClient from '../../api/axios-client';
import type { ApiError } from '../../api/useApi';
import type { HousekeepingRoomStatus } from '../../pages/housekeeping/types/housekeeping';
import { invaliderMenage } from './useActionsMenage';

/**
 * « Contrôles » de la gouvernante : le plateau de l'hôtel, puis valider ou refuser.
 *
 * Mêmes appels que le web (`useHousekeepingBoard`, `useValidateInspection`,
 * `useRejectInspection`), recopiés pour la frontière du socle. Le serveur accepte
 * une chambre « À contrôler » ou « Libre propre » (recontrôle du lendemain).
 */

/** Le web sonde le plateau toutes les 60 s ; même rythme ici. */
export const SONDAGE_CONTROLES_MS = 60_000;

export function useChambresDeLHotel(facilityId: number | null) {
    return useQuery<HousekeepingRoomStatus[]>(
        ['/housekeeping/board', { facilityId, ecran: 'controles' }],
        () => axiosClient.get<HousekeepingRoomStatus[]>('/housekeeping/board').then((res) => res.data),
        { enabled: facilityId !== null, refetchInterval: SONDAGE_CONTROLES_MS },
    );
}

/** 422 : la chambre a changé entre-temps (déjà validée par une autre gouvernante…). */
export interface ErreurControle extends ApiError {
    response?: { status?: number; data: { message: string; errors: Record<string, string[]> } };
}

export function useValiderControle() {
    const queryClient = useQueryClient();
    return useMutation<unknown, ErreurControle, { serviceId: number }>(
        ({ serviceId }) => axiosClient.post(`/housekeeping/rooms/${serviceId}/inspection/validate`),
        { onSettled: () => invaliderMenage(queryClient) },
    );
}

export function useRefuserControle() {
    const queryClient = useQueryClient();
    return useMutation<unknown, ErreurControle, { serviceId: number; reason: string }>(
        ({ serviceId, reason }) =>
            axiosClient.post(`/housekeeping/rooms/${serviceId}/inspection/reject`, { reason, assigned_user_id: null }),
        { onSettled: () => invaliderMenage(queryClient) },
    );
}
